import React, { useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import { DataView, EmptyState, PickerRulesPanel, PurchaseConfirmDialog } from "/imports/ui/components/shared";
import { ClubBookingCancelDialog, PackContinuationBanner } from "/imports/ui/components/booking";
import { pickerContinuationCopy } from "/imports/libs/packContinuation";
import UpfrontSlotsBuilderView from "../UpfrontSlotsBuilderView";
import useUpfrontSessions from "./useUpfrontSessions";
import { sessionGridSx } from "./upfrontSessionsHelpers";
import SessionsToolbar from "./components/SessionsToolbar";
import SessionCard from "./components/SessionCard";
import SessionsStickyBar from "./components/SessionsStickyBar";
import OutsideValidityRun from "./components/OutsideValidityRun";

const OUTSIDE_VALIDITY = "Outside pack validity";

function UpfrontSessionsView() {
  const {
    navigate,
    slug,
    currency,
    isAuthenticated,
    pkg,
    opt,
    locationId,
    backTo,
    mode,
    sessionsN,
    minPicks,
    isComposerCategory,
    catalogLoading,
    catalogError,
    initialLoad,
    loadingMore,
    sessionsError,
    sessions,
    hiddenCount,
    eligibleCount,
    needsAssessment,
    showSessionLevel,
    notEnoughEligible,
    grouped,
    locationOptions,
    venueFilter,
    setVenueFilter,
    selected,
    selectedSessions,
    existingTicked,
    selectable,
    fitsWindow,
    toggle,
    clearPicks,
    clubCancel,
    requestCancel,
    notIncluded,
    addClubClass,
    selectNext,
    repeatWeekly,
    lastPick,
    spanError,
    memberStatus,
    effectivePrice,
    regularPrice,
    memberNote,
    confirmOpen,
    wallets,
    setConfirmOpen,
    buying,
    doPurchase,
    missingVariant,
    countLabel,
    canContinue,
    confirmLines,
    confirmNote,
    packRules,
    packWindowNote,
    packContinuation,
    continueAnchorId
  } = useUpfrontSessions();

  // The list opens at the class the next pack carries on from, once — after
  // that the client's own scrolling is never fought.
  const continueAnchor = useRef(null);
  const openedAtContinue = useRef(false);
  const attachContinueAnchor = (node) => {
    continueAnchor.current = node;
    if (!node || openedAtContinue.current) return;
    openedAtContinue.current = true;
    node.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  const continuationCopy = pickerContinuationCopy(packContinuation, opt?.linkedCategoryType);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [openRuns, setOpenRuns] = useState(() => new Set());
  const toggleRun = (key) =>
    setOpenRuns((cur) => {
      const next = new Set(cur);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // After every hook: the login effect in the hook is redirecting — render nothing.
  if (!isAuthenticated) return null;

  // Private-lesson / court packs: hand over to the slot-composer builder
  // (classes continue below with the timetable picker).
  if (isComposerCategory && pkg && opt) {
    return <UpfrontSlotsBuilderView pkg={pkg} opt={opt} locationId={locationId} backTo={backTo} memberStatus={memberStatus} />;
  }

  const cardState = (c) => {
    const isPicked = selected.has(c._id);
    const disabled = !isPicked && !selectable(c);
    const full = c.spotsAvailable != null && c.spotsAvailable <= 0 && !c.isJoined;
    const reason = c.joinedUnbilled
      ? "Already booked — this pack pays for it"
      : c.isJoined
        ? "You're already booked"
        : full
          ? "Full"
          : c.eligibility?.eligible === false
            ? c.eligibility.reason || "Not eligible"
            : disabled && !fitsWindow(c)
              ? OUTSIDE_VALIDITY
              : null;
    return { isPicked, disabled, reason };
  };

  const renderCard = (c) => {
    const { isPicked, disabled, reason } = cardState(c);
    return (
      <SessionCard
        key={c._id}
        session={c}
        isPicked={isPicked}
        disabled={disabled}
        reason={reason}
        paid={c.isJoined && c.joinedPaid}
        showLevel={showSessionLevel}
        onToggle={() => toggle(c)}
        onCancel={c.joinedUnbilled && !isPicked ? () => requestCancel(c) : undefined}
        cardRef={c._id === continueAnchorId ? attachContinueAnchor : undefined}
      />
    );
  };

  // On a phone, two or more classes in a row that the pack can't reach fold
  // into one line. Full, booked and paid cards are never folded: they explain a
  // gap in the week the client can see.
  const segmentsOf = (items) => {
    if (!isMobile) return items.map((c) => ({ c }));
    const out = [];
    let run = [];
    const flush = () => {
      if (run.length >= 2) out.push({ run, key: `run:${run[0]._id}` });
      else run.forEach((c) => out.push({ c }));
      run = [];
    };
    for (const c of items) {
      if (cardState(c).reason === OUTSIDE_VALIDITY) run.push(c);
      else {
        flush();
        out.push({ c });
      }
    }
    flush();
    return out;
  };

  return (
    <Box sx={{ pb: { xs: "9rem", md: "5.5rem" }, maxWidth: "60rem", mx: "auto" }}>
      <Box mb={2}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
          <IconButton size="small" onClick={() => navigate(backTo || `/${slug}/plans`)} aria-label="Back to packages" sx={{ ml: -0.5 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight={700}>
            Pick your sessions
          </Typography>
        </Box>
        {pkg && opt && (
          <Typography variant="body2" color="textSecondary">
            {pkg.name}
            {opt.sessionDuration ? ` · ${opt.sessionDuration} min` : ""} —{" "}
            {mode === "all"
              ? `choose all ${sessionsN} class dates to book with your purchase.`
              : `choose at least one class date to start with — the rest of your ${sessionsN} sessions can be booked later.`}
          </Typography>
        )}
        {/* The club's booking window ends the list until the first pick is made;
            after it, the pack's own validity does. Say which, so a list that
            stops short doesn't read as "nothing else is available". */}
        {packWindowNote && (
          <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: "primary.main", fontWeight: 600 }}>
            {packWindowNote}
          </Typography>
        )}
      </Box>

      <DataView loading={catalogLoading || initialLoad} error={catalogError || sessionsError} data={[1]} loadingMessage="Loading sessions...">
        {missingVariant ? (
          <EmptyState title="Package not found" description="This package is no longer available — head back to Packages and pick again." />
        ) : sessions.length === 0 ? (
          <EmptyState
            title="No upcoming sessions"
            description="There are no bookable sessions for this package right now — check back later or contact the club."
          />
        ) : (
          <>
            {/* Every action here picks sessions; with nothing pickable they are all no-ops. */}
            {eligibleCount > 0 && (
              <SessionsToolbar
                countLabel={countLabel}
                selectedCount={selectedSessions.length}
                minPicks={minPicks}
                sessionsN={sessionsN}
                lastPick={lastPick}
                onSelectNext={selectNext}
                onRepeatWeekly={repeatWeekly}
                locationOptions={locationOptions}
                venueFilter={venueFilter}
                onVenueFilter={setVenueFilter}
              />
            )}

            {/* Unlike the recommendation it replaces, these are rules rather than a
                nudge, so they stay put instead of clearing on the first pick. */}
            {!notEnoughEligible && <PickerRulesPanel title="Before you buy" rules={packRules} />}

            {continuationCopy && (
              <PackContinuationBanner
                {...continuationCopy}
                onAction={continueAnchorId ? () => continueAnchor.current?.scrollIntoView({ behavior: "smooth", block: "center" }) : undefined}
                sx={{ mb: 2 }}
              />
            )}

            {/* The first pick widens the horizon, which re-fetches — say so
                rather than blanking the list the client is working in. */}
            {loadingMore && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="textSecondary">
                  Opening the rest of the dates your pack covers...
                </Typography>
              </Box>
            )}

            {spanError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {spanError}
              </Alert>
            )}

            {/* Hiding the ineligible cards takes their explanation with them, so say it
                once here — and point at the assessment when that is what is missing. */}
            {notEnoughEligible && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {needsAssessment
                  ? "Your level for this sport hasn't been set yet, so these classes aren't open to you. Ask the club for an assessment and they'll show up here."
                  : minPicks > 1
                    ? `Only ${eligibleCount} of the upcoming sessions match your level, and this pack books all ${sessionsN} at purchase. Contact the club and they'll sort the rest out with you.`
                    : "None of the upcoming sessions match your level right now. Contact the club and they'll help you find a class."}
              </Alert>
            )}

            {hiddenCount > 0 && !notEnoughEligible && (
              <Typography variant="caption" color="textSecondary" sx={{ display: "block", mb: 2 }}>
                Showing the sessions that match your level — {hiddenCount} other{hiddenCount === 1 ? "" : "s"} hidden.
              </Typography>
            )}

            {/* Filtered down to nothing: the list would otherwise just end, with the
                venue chip the only clue as to why. */}
            {grouped.length === 0 && venueFilter && (
              <Alert
                severity="info"
                sx={{ mb: 2 }}
                action={
                  <Button color="inherit" size="small" onClick={() => setVenueFilter(null)} sx={{ textTransform: "none" }}>
                    Show all
                  </Button>
                }
              >
                No sessions at this location within the dates your pack covers.
              </Alert>
            )}

            <Stack spacing={2.5}>
              {grouped.map((group) => (
                <Box key={group.month}>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1, fontWeight: 700 }}>
                    {group.month}
                  </Typography>
                  <Box sx={sessionGridSx}>
                    {segmentsOf(group.items).map((seg) =>
                      seg.run ? (
                        <OutsideValidityRun key={seg.key} sessions={seg.run} expanded={openRuns.has(seg.key)} onToggle={() => toggleRun(seg.key)}>
                          {seg.run.map(renderCard)}
                        </OutsideValidityRun>
                      ) : (
                        renderCard(seg.c)
                      )
                    )}
                  </Box>
                </Box>
              ))}
            </Stack>
          </>
        )}
      </DataView>

      {/* Sticky action bar: the pick count and the way forward stay in reach on
          a long list. */}
      {!missingVariant && sessions.length > 0 && (
        <SessionsStickyBar
          currency={currency}
          effectivePrice={effectivePrice}
          regularPrice={regularPrice}
          memberNote={memberNote}
          countLabel={countLabel}
          canContinue={canContinue}
          selectedSessions={selectedSessions}
          showLocation={locationOptions.length > 1}
          existingTicked={existingTicked}
          onRemove={toggle}
          onClear={clearPicks}
          notIncluded={notIncluded}
          onAddClubClass={addClubClass}
          onCancelClubClass={requestCancel}
          minPicks={minPicks}
          sessionsN={sessionsN}
          mode={mode}
          onContinue={() => setConfirmOpen(true)}
        />
      )}

      <ClubBookingCancelDialog cancel={clubCancel} />

      <PurchaseConfirmDialog
        open={confirmOpen}
        name={pkg?.name}
        lines={confirmLines}
        note={confirmNote}
        notices={packRules}
        location={null}
        price={effectivePrice}
        regularPrice={regularPrice}
        currency={currency || ""}
        wallets={wallets}
        loading={buying}
        onConfirm={doPurchase}
        onClose={() => setConfirmOpen(false)}
      />
    </Box>
  );
}

export default UpfrontSessionsView;
